import { CreateForm, CreateFormProps, FormGuard, useScopedSelector } from 'ia-react-core';
import React, { useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { StyledEmphasisBlock, StyledPrimaryButton, StyledSecondaryButton, StyledActionBar, StyledBlockGroup, StyledFormDiv, StyledIcon } from './Form.styles';
import InformationsSection from '../InformationsSection';
import mapControlErrors from './mapControlErrors';
import mapFormErrors from './mapFormErrors';
import { BFFBadRequest } from '../../interfaces/BFFBadRequest';
import FormPageTitle from '../FormPageTitle';
import redirectTo from '../../utils/redirectTo';
import FormContext from './FormContext';
export interface FormProps<
  Values,
  Data,
  Error = {}
> extends Omit<CreateFormProps<Values, Data, Error>, 'onCancel'> {
  formTitle?: string;
  infoMessageText?: string;
  submitDisabled?: boolean;
  isBlock?: boolean;
  submitButtonText?: string;
  cancelButtonText?: string;
  modalErrorMessage?: string;
  confirmationPath?: string | ((rep: Data) => string);
  formGuard?: boolean;
  controlErrorMapping?: { [name: string]: string };
  children: React.ReactNode;
  onCancel?: () => void;
}

const Form = <Values extends object, Data, Error = {}>(
  {
    isBlock = true,
    formTitle,
    infoMessageText,
    submitDisabled,
    submitButtonText,
    cancelButtonText,
    modalErrorMessage,
    confirmationPath,
    formGuard = true,
    controlErrorMapping = {},
    onCancel,
    onSuccess,
    name,
    children,
    ...props
  }: FormProps<Values, Data, Error>) => {
  const ctx = useContext(FormContext);
  const { t } = useTranslation('Form');
  const navigate = useNavigate();
  const isSubmitting: boolean = useScopedSelector(`${name}.isSubmitting`);

  const handleSuccess = useCallback(((rep: { data: Data }) => {
    if (confirmationPath) {
      redirectTo((typeof confirmationPath === 'function') ? confirmationPath(rep.data) : confirmationPath, navigate);
    }
  }), [confirmationPath, navigate]);

  const handleCancel = useCallback(() => {
    redirectTo(ctx?.returnPage, navigate);
  }, [ctx?.returnPage, navigate]);

  const modalErrorMessages = modalErrorMessage ?
    {
      networkErrorMessage: modalErrorMessage,
      badRequestMessage: modalErrorMessage,
      serverErrorMessage: modalErrorMessage,
      serviceUnavailableMessage: modalErrorMessage,
    } : {};

  return (
    <>
      {formTitle && <FormPageTitle>{formTitle}</FormPageTitle>}
      <StyledFormDiv>
        <CreateForm<Values, Data>
          name={name}
          defaultCol={12}
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          controlErrorsSelector={(rep: BFFBadRequest) => mapControlErrors(rep, controlErrorMapping)}
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          formErrorsSelector={(rep: BFFBadRequest) => mapFormErrors(rep, controlErrorMapping, t('backendValidationErrorMessage'), t('backendValidationUnexpectedErrorMessage'))}
          valuesSrc={`${name}Values`}
          disabled={isSubmitting}
          onSuccess={onSuccess || handleSuccess}
          {...modalErrorMessages}
          {...props}
        >
          {formGuard && <FormGuard Icon={StyledIcon} />}
          {isBlock ? (
            <StyledBlockGroup p-b={4}>
              <StyledEmphasisBlock white>
                {infoMessageText && <InformationsSection informationsLabel={infoMessageText} />}
                {children}
              </StyledEmphasisBlock>
            </StyledBlockGroup>
          )
            : (
              <>
                {infoMessageText && <InformationsSection informationsLabel={infoMessageText} />}
                {children}
              </>
            )}
          <StyledActionBar m-b={36}>
            <StyledPrimaryButton busy={isSubmitting} disabled={submitDisabled === true}>
              {submitButtonText || t('saveButtonText')}
            </StyledPrimaryButton>
            <StyledSecondaryButton disabled={isSubmitting} onClick={onCancel || handleCancel}>
              {cancelButtonText || t('cancelButtonText')}
            </StyledSecondaryButton>
          </StyledActionBar>
        </CreateForm>
      </StyledFormDiv>
    </>
  );
};

export default Form;
